import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChangePlanDrawer } from '../../components/change-plan-drawer.jsx';
import { TezusContext } from '../../components/context.jsx';
import { GlobalSearch } from '../../components/global-search.jsx';
import { ImpersonationModal, ActiveImpersonationBannerV2 } from '../../components/impersonation-modal.jsx';
import {
  FEATURE_IMPERSONATION,
  saGetShowFuturePages,
  saIsFuturePage,
  saSetShowFuturePages,
} from '../../components/mvp-config.jsx';
import { NotificationsPanel } from '../../components/notifications.jsx';
import { isRealTenantsMode, mapAuthUserToProfileDrawer, _notifStore } from '../../components/services.jsx';
import { Sidebar, Topbar, ImpersonateBanner, ConfirmModal, Toast } from '../../components/shell.jsx';
import { OperationalStatusReasonModal } from '../../components/tenant-status.jsx';
import { UserEditorDrawer } from '../../components/user-editor-drawer.jsx';
import { NotFoundPage } from '../pages/NotFoundPage.jsx';
import { paths } from '../router/app-paths.js';
import { discardSavedRouteKey } from '../router/legacy-saved-route-migration.js';
import { matchStoreByTenantParam, urlToLegacyPage } from '../router/paths.js';
import { getBreadcrumbTrail, getPageLabel } from '../router/route-meta.js';

const AppShellContext = React.createContext(null);

export function useAppShell() {
  const ctx = React.useContext(AppShellContext);
  if (!ctx) throw new Error('useAppShell must be used within ProtectedAppLayout');
  return ctx;
}

/**
 * Shared authenticated chrome: Sidebar, Topbar, overlays, Outlet for page content.
 * Phase 3: URL is the sole navigation source — no onNav adapter, no tezus-route writes,
 * no NavigationTransitProvider.
 */
export function ProtectedAppLayout({
  authUser,
  onLogout,
  theme,
  setTheme,
  showToast,
  toast,
  shellHandlers,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    stores, updateStore, addStore, refetchStores,
    storesLoading, storesError,
    internalUsers, setInternalUsers, auditLog, logAction,
  } = React.useContext(TezusContext);

  const useRealStores = typeof isRealTenantsMode === 'function' && isRealTenantsMode();

  const [impersonating, setImpersonating] = React.useState(null);
  const [impersonModal, setImpersonModal] = React.useState(null);
  const [confirmState, setConfirmState] = React.useState(null);
  const [operationalModal, setOperationalModal] = React.useState(null);
  const [operationalSaving, setOperationalSaving] = React.useState(false);
  const [storesListVersion, setStoresListVersion] = React.useState(0);
  const [planDrawerStore, setPlanDrawerStore] = React.useState(null);
  const [notifsOpen, setNotifsOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = React.useState(false);
  const [statusActionLoading, setStatusActionLoading] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [tweaks, setTweaks] = React.useState({ tableLayout: 'default' });
  const [notifVersion, setNotifVersion] = React.useState(0);
  const [showFuturePages, setShowFuturePages] = React.useState(() => (
    typeof saGetShowFuturePages === 'function' ? saGetShowFuturePages() : true
  ));

  const parsed = urlToLegacyPage(location.pathname) || { page: 'dashboard' };
  const page = parsed.page;
  const tenantId = parsed.tenantId || null;
  const tabSegment = parsed.tabSegment || null;
  const eventId = parsed.eventId || null;

  // Resolve store for chrome (breadcrumbs) from list or optional location.state cache.
  const chromeStore = React.useMemo(() => {
    if (!(page === 'loja' || page === 'cockpit') || !tenantId) return null;
    const fromState = location.state?.store;
    if (fromState && matchStoreByTenantParam([fromState], tenantId)) return fromState;
    return matchStoreByTenantParam(stores, tenantId);
  }, [page, tenantId, stores, location.state]);

  // Explicit URL wins: drop leftover tezus-route without redirecting.
  React.useEffect(() => {
    discardSavedRouteKey();
  }, [location.pathname]);

  React.useEffect(() => {
    const store = _notifStore;
    if (!store) return;
    const unsub = store.subscribe(() => setNotifVersion((v) => v + 1));
    return unsub;
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === '__activate_edit_mode') setEditMode(true);
      if (e.data?.type === '__deactivate_edit_mode') setEditMode(false);
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);

  const handleShowFuturePagesChange = React.useCallback((next) => {
    const value = typeof saSetShowFuturePages === 'function'
      ? saSetShowFuturePages(next)
      : next === true;
    setShowFuturePages(value);
    if (!value) {
      setNotifsOpen(false);
      if (typeof saIsFuturePage === 'function' && saIsFuturePage(page)) {
        navigate(paths.dashboard(), { replace: true });
      }
    }
  }, [navigate, page]);

  const routeForChrome = React.useMemo(() => ({
    page,
    store: chromeStore,
    tenantId,
  }), [page, chromeStore, tenantId]);

  const handleOpenProfile = React.useCallback(async () => {
    if (shellHandlers?.handleOpenProfile) {
      await shellHandlers.handleOpenProfile();
      setProfileDrawerOpen(true);
      return;
    }
    setProfileDrawerOpen(true);
  }, [shellHandlers]);

  // Inbox operacional é MVP; sino/painel não dependem de “páginas futuras”.
  const notificationsUiEnabled = true;
  // notifVersion forces a re-read of the store after mark-read / reload.
  const storeUnreadCount = React.useMemo(
    () => (notificationsUiEnabled && _notifStore ? _notifStore.getUnreadCount() : 0),
    [notificationsUiEnabled, notifVersion],
  );
  // Topbar hides the badge while the panel is open; sidebar keeps the real count.
  const unreadCount = (!notificationsUiEnabled || notifsOpen) ? 0 : storeUnreadCount;
  const hasSLAAlert = notificationsUiEnabled && _notifStore
    ? _notifStore.hasSLAAlert()
    : false;

  const shellValue = React.useMemo(() => ({
    showToast,
    theme,
    setTheme,
    showFuturePages,
    setShowFuturePages: handleShowFuturePagesChange,
    page,
    tenantId,
    stores,
    updateStore,
    addStore,
    refetchStores,
    storesLoading,
    storesError,
    internalUsers,
    setInternalUsers,
    auditLog,
    logAction,
    useRealStores,
    storesListVersion,
    setStoresListVersion,
    setPlanDrawerStore,
    setImpersonModal,
    setConfirmState,
    setOperationalModal,
    setStatusActionLoading,
    statusActionLoading,
    tweaks,
    shellHandlers,
    authUser,
  }), [
    showToast,
    theme,
    showFuturePages,
    handleShowFuturePagesChange,
    page,
    tenantId,
    stores,
    updateStore,
    addStore,
    refetchStores,
    storesLoading,
    storesError,
    internalUsers,
    auditLog,
    logAction,
    useRealStores,
    storesListVersion,
    statusActionLoading,
    tweaks,
    shellHandlers,
    authUser,
  ]);

  return (
    <AppShellContext.Provider value={shellValue}>
      <div className="app">
        <Sidebar
          route={routeForChrome}
          activePage={page}
          onLogout={onLogout}
          onProfile={handleOpenProfile}
          authUser={authUser}
          showFuturePages={showFuturePages}
          onShowFuturePagesChange={handleShowFuturePagesChange}
          unreadNotifCount={storeUnreadCount}
        />
        <div style={{ minWidth: 0 }}>
          {impersonating && (
            typeof ActiveImpersonationBannerV2 !== 'undefined'
              ? (
                <ActiveImpersonationBannerV2
                  store={impersonating}
                  sessionId="imp_active"
                  readOnly={false}
                  onExit={() => {
                    setImpersonating(null);
                    showToast('Modo impersonação encerrado');
                    logAction({
                      acao: 'Login como lojista encerrado',
                      loja: impersonating.name,
                      lojaId: impersonating.id,
                      kind: 'info',
                    });
                  }}
                />
              )
              : (
                <ImpersonateBanner
                  store={impersonating}
                  onExit={() => {
                    setImpersonating(null);
                    showToast('Modo impersonação encerrado');
                  }}
                />
              )
          )}
          <Topbar
            route={routeForChrome}
            pageLabel={getPageLabel(page)}
            breadcrumbs={getBreadcrumbTrail({
              page,
              tabSegment,
              store: chromeStore,
              tenantId,
              eventId,
            })}
            theme={theme}
            onToggleTheme={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            onBell={notificationsUiEnabled ? () => setNotifsOpen((o) => !o) : undefined}
            unreadCount={unreadCount}
            hasSLAAlert={hasSLAAlert}
            onSearch={() => setSearchOpen(true)}
          />
          <Outlet />
        </div>

        <ConfirmModal
          open={!!confirmState}
          onClose={() => !statusActionLoading && setConfirmState(null)}
          onConfirm={confirmState?.onConfirm}
          title={confirmState?.title}
          body={confirmState?.body}
          confirmLabel={confirmState?.confirmLabel}
          danger={confirmState?.danger}
          requireReason={confirmState?.requireReason}
          minReasonLength={confirmState?.minReasonLength || 5}
          confirmLoading={statusActionLoading}
        />

        {typeof OperationalStatusReasonModal !== 'undefined' && (
          <OperationalStatusReasonModal
            open={!!operationalModal}
            action={operationalModal?.action}
            tenantName={operationalModal?.store?.name}
            onClose={() => !operationalSaving && setOperationalModal(null)}
            onConfirm={async ({ reason }) => {
              if (shellHandlers?.handleOperationalConfirm) {
                await shellHandlers.handleOperationalConfirm({
                  reason,
                  operationalModal,
                  setOperationalModal,
                  setOperationalSaving,
                });
              }
            }}
          />
        )}

        <ChangePlanDrawer
          open={!!planDrawerStore}
          store={planDrawerStore}
          onClose={() => setPlanDrawerStore(null)}
          onConfirm={({ plan, apply }) => {
            if (shellHandlers?.handleChangePlanConfirm) {
              shellHandlers.handleChangePlanConfirm({ plan, apply, planDrawerStore, setPlanDrawerStore });
            }
          }}
        />

        {notificationsUiEnabled && (
          <NotificationsPanel
            open={notifsOpen}
            onClose={() => setNotifsOpen(false)}
            onToast={showToast}
            stores={stores}
          />
        )}

        {searchOpen && (
          <GlobalSearch
            stores={stores}
            internalUsers={internalUsers}
            storesLoading={storesLoading}
            useRealStores={useRealStores}
            onClose={() => setSearchOpen(false)}
          />
        )}

        <UserEditorDrawer
          open={profileDrawerOpen}
          user={profileDrawerOpen && authUser ? mapAuthUserToProfileDrawer(authUser) : null}
          onClose={() => setProfileDrawerOpen(false)}
          onSave={() => { setProfileDrawerOpen(false); showToast('Perfil atualizado'); }}
        />

        <Toast message={toast} />

        {FEATURE_IMPERSONATION === true && typeof ImpersonationModal !== 'undefined' && (
          <ImpersonationModal
            open={!!impersonModal}
            store={impersonModal}
            onClose={() => setImpersonModal(null)}
            onConfirm={({ store }) => {
              setImpersonating(store);
              setImpersonModal(null);
              showToast(`Sessão iniciada em ${store.name}`);
            }}
            logAction={logAction}
          />
        )}

        {editMode && (
          <div className="tweaks">
            <h4>Tweaks</h4>
            <div className="tweak">
              <label>Layout da tabela de Lojas</label>
              <div className="seg">
                <button className={tweaks.tableLayout === 'default' ? 'active' : ''} onClick={() => setTweaks((t) => ({ ...t, tableLayout: 'default' }))}>Padrão</button>
                <button className={tweaks.tableLayout === 'compact' ? 'active' : ''} onClick={() => setTweaks((t) => ({ ...t, tableLayout: 'compact' }))}>Compacto</button>
                <button className={tweaks.tableLayout === 'cards' ? 'active' : ''} onClick={() => setTweaks((t) => ({ ...t, tableLayout: 'cards' }))}>Cards</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                {page !== 'lojas' && (
                  <>
                    Vá até{' '}
                    <Link to={paths.lojas()} style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Lojas</Link>
                    {' '}para ver.
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShellContext.Provider>
  );
}

export function NotFoundRoute() {
  const navigate = useNavigate();
  return (
    <NotFoundPage
      onGoDashboard={() => navigate(paths.dashboard())}
      onGoBack={() => {
        if (window.history.length > 1) navigate(-1);
        else navigate(paths.dashboard());
      }}
    />
  );
}

export { AppShellContext };
